import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useCartStore } from "./cartStore";
import { findVariant, getCartItemQuantity, getDefaultVariant, getMaxQuantity } from "./shopVariants";
import type { ShopProduct } from "./types";

/**
 * "Add to cart" for a catalog product. Adds the default variant when the product isn't in the cart
 * yet, otherwise adds one more unit of the variant already chosen, up to the product's limit.
 */
export const useAddToCart = (product: ShopProduct | null) => {
  const { t } = useTranslation("shop");
  const addItem = useCartStore((state) => state.addToCart);
  const updateQuantity = useCartStore((state) => state.updateQuantity);
  const cartItem = useCartStore((state) =>
    product ? state.items.find((item) => item.stripeProductId === product.stripeProductId) : undefined
  );

  const addToCart = () => {
    if (!product) return;

    if (!cartItem) {
      addItem(product.stripeProductId, getDefaultVariant(product).stripePriceId);
      toast.success(t("{{title}} added to cart", { title: product.title }));
      return;
    }

    const maxQuantity = getMaxQuantity(product, findVariant(product, cartItem.stripePriceId));
    const quantity = getCartItemQuantity(cartItem, product);
    if (quantity < maxQuantity) {
      updateQuantity(product.stripeProductId, quantity + 1, maxQuantity);
      toast.success(t("{{title}} added to cart", { title: product.title }));
      return;
    }

    toast.info(
      maxQuantity > 1
        ? t("You can add up to {{max}} of {{title}}", { max: maxQuantity, title: product.title })
        : t("{{title}} is already in your cart", { title: product.title })
    );
  };

  return { addToCart };
};
